import React, { useEffect } from "react";
import { Box, Typography, Container, Grid, Button, Link } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
// Assuming this path based on previous findings, adjust if necessary
import heroBg from "../assets/nextix/learnmore-banner.jpg";

const BusinessSection = ({ title, description, link, delay }) => {
    const navigate = useNavigate();
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: delay }}
            style={{ marginBottom: "2rem" }}
        >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: "#375ca8", textAlign: "start" }}>
                {title}
            </Typography>
            <Typography paragraph sx={{ color: "#666", lineHeight: 1.8, mb: 1, textAlign: "start" }}>
                {description}
            </Typography>
            <Box sx={{ textAlign: "start" }}>
                <Link
                    component="button"
                    onClick={() => navigate(link)}
                    sx={{
                        color: "#375ca8",
                        fontWeight: "bold",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" }
                    }}
                >
                    Learn More.
                </Link>
            </Box>
        </motion.div>
    );
};

const BusinessesScreen = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box sx={{ backgroundColor: "#fff" }}>
            {/* BANNER IMAGE */}
            <Container maxWidth="lg" sx={{ pt: 12, pb: 4 }}>
                <Box sx={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    position: "relative",
                    height: "300px", // Fixed height for banner
                    backgroundColor: "#f0f0f0" // Fallback color
                }}>
                    <img
                        src={heroBg}
                        alt="Businesses"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block"
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }} // Hide if not found, rely on fallback bg or text
                    />
                </Box>
            </Container>

            {/* MAIN CONTENT */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Grid container spacing={6}>
                    {/* Left Column - Main Content */}
                    <Grid item xs={12} md={8}>
                        <BusinessSection
                            title="Identity"
                            description="End-to-end solutions for physical and digital identity – biometrics, identification cards, customization, printing, audit and data analytics."
                            link="/business-identity"
                            delay={0}
                        />
                        <BusinessSection
                            title="Cloud"
                            description="Specialized, scalable and secure cloud for government and enterprise."
                            link="/business-cloud"
                            delay={0.1}
                        />
                        <BusinessSection
                            title="Fintech"
                            description="Powerful platforms for financial inclusion – digital banking, microfinance, virtual currency exchange."
                            link="/business-fintech"
                            delay={0.2}
                        />
                        <BusinessSection
                            title="Authentication"
                            description="Anti-counterfeit technologies for physical and digital items – blockchain, digital twinning, permanent storage, artificial intelligence."
                            link="/business-authentication"
                            delay={0.3}
                        />
                        <BusinessSection
                            title="Digital Infrastructure"
                            description="Easy-to-manage IT infrastructure aligned with your business needs."
                            link="/business-digital-infrastructure"
                            delay={0.4}
                        />
                    </Grid>

                    {/* Right Column - Sidebar */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: { md: "sticky" }, top: 100, textAlign: 'start' }}>
                            {/* Send Us A Message */}
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#375ca8" }}>
                                    Send Us A Message
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 3, color: "#666", lineHeight: 1.6 }}>
                                    For more inquiries about our Products, send us a message
                                </Typography>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{
                                        backgroundColor: "#82c9ed",
                                        color: "white",
                                        fontWeight: 700,
                                        py: 1.5,
                                        mb: 4,
                                        "&:hover": { backgroundColor: "#6bb5d9" }
                                    }}
                                    href="/contact-us" // Link to contact page or anchor
                                >
                                    CONTACT OUR SALES NOW
                                </Button>
                            </motion.div>

                            {/* Contact Information */}
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: "#1c1c1c" }}>
                                    Email Us:
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                                        <strong>Inquiry:</strong>{" "}
                                        <Link href="mailto:into@nextix.co" sx={{ color: "#375ca8", textDecoration: "none" }}>
                                            into@nextix.co
                                        </Link>
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                                        <strong>Support:</strong>{" "}
                                        <Link href="mailto:support@nextix.co" sx={{ color: "#375ca8", textDecoration: "none" }}>
                                            support@nextix.co
                                        </Link>
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                                        <strong>Privacy:</strong>{" "}
                                        <Link href="mailto:dpo@nextix.co" sx={{ color: "#375ca8", textDecoration: "none" }}>
                                            dpo@nextix.co
                                        </Link>
                                    </Typography>
                                </Box>

                                {/* White Paper Resources */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: "#1c1c1c" }}>
                                    White Paper
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Link href="#" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "underline" } }}>
                                        OFW-Contact-Center
                                    </Link>
                                    <Link href="#" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "underline" } }}>
                                        Global-Video-whitepaper
                                    </Link>
                                    <Link href="#" sx={{ color: "#375ca8", textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "underline" } }}>
                                        City-wide-whitepaper
                                    </Link>
                                </Box>
                            </motion.div>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default BusinessesScreen;
